import React, { useState, useMemo, useRef } from 'react';
import { 
  Users, 
  Package, 
  Ruler, 
  Wrench,
  Route,
  Printer,
  ArrowLeft,
  Search,
  Plus,
  Edit,
  Trash2,
  X,
  Scale,
  Cog,
  Factory,
  PauseCircle,
  Palette,
  ChevronUp,
  ChevronDown,
  ChevronsUpDown,
  Upload,
  Download,
  CheckCircle,
  AlertCircle,
  Briefcase,
  ChevronRight,
  ListTree,
  Radar,
  Building2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import axios from 'axios';

interface CatalogCard {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  color: string;
  bgColor: string;
}

const catalogCards: CatalogCard[] = [
  {
    id: 'clients',
    title: 'Clientes',
    description: 'Gestión de clientes y contactos comerciales',
    icon: <Users className="w-10 h-10" />,
    color: 'text-blue-600',
    bgColor: 'bg-blue-500',
  },
  {
    id: 'products',
    title: 'Productos',
    description: 'Catálogo de productos y artículos',
    icon: <Package className="w-10 h-10" />,
    color: 'text-green-600',
    bgColor: 'bg-green-500',
  },
  {
    id: 'units',
    title: 'Unidades de Medida',
    description: 'Unidades para cuantificar productos',
    icon: <Ruler className="w-10 h-10" />,
    color: 'text-purple-600',
    bgColor: 'bg-purple-500',
  },
  {
    id: 'equipment',
    title: 'Equipos',
    description: 'Equipos y maquinaria de producción',
    icon: <Wrench className="w-10 h-10" />,
    color: 'text-orange-600',
    bgColor: 'bg-orange-500',
  },
  {
    id: 'weight-records',
    title: 'Registros de Peso',
    description: 'Conceptos de registro de peso en órdenes',
    icon: <Scale className="w-10 h-10" />,
    color: 'text-amber-600',
    bgColor: 'bg-amber-500',
  },
  {
    id: 'process-types',
    title: 'Tipos de Procesos',
    description: 'Clasificación de tipos de procesos productivos',
    icon: <Cog className="w-10 h-10" />,
    color: 'text-cyan-600',
    bgColor: 'bg-cyan-500',
  },
  {
    id: 'manufacturing-processes',
    title: 'Procesos de Fabricación',
    description: 'Catálogo de procesos de fabricación',
    icon: <Factory className="w-10 h-10" />,
    color: 'text-violet-600',
    bgColor: 'bg-violet-500',
  },
  {
    id: 'manufacturing-routes',
    title: 'Rutas de Fabricación',
    description: 'Rutas y procesos de manufactura',
    icon: <Route className="w-10 h-10" />,
    color: 'text-red-600',
    bgColor: 'bg-red-500',
  },
  {
    id: 'pause-reasons',
    title: 'Motivos de Pausa',
    description: 'Motivos de pausa durante la ejecución de procesos',
    icon: <PauseCircle className="w-10 h-10" />,
    color: 'text-rose-600',
    bgColor: 'bg-rose-500',
  },
  {
    id: 'colors',
    title: 'Colores',
    description: 'Catálogo de colores base para especificaciones de proceso',
    icon: <Palette className="w-10 h-10" />,
    color: 'text-pink-600',
    bgColor: 'bg-pink-500',
  },
  {
    id: 'print-types',
    title: 'Tipos de Impresión',
    description: 'Catálogo de tipos de impresión para Órdenes de Grabado',
    icon: <Printer className="w-10 h-10" />,
    color: 'text-indigo-600',
    bgColor: 'bg-indigo-500',
  },
  {
    id: 'photocell-types',
    title: 'Tipos de Fotocelda',
    description: 'Catálogo de tipos de fotocelda para Especificación del Proceso',
    icon: <Radar className="w-10 h-10" />,
    color: 'text-sky-600',
    bgColor: 'bg-sky-500',
  },
  {
    id: 'employees',
    title: 'Empleados',
    description: 'Catálogo de empleados y su puesto de trabajo',
    icon: <Briefcase className="w-10 h-10" />,
    color: 'text-teal-600',
    bgColor: 'bg-teal-500',
  },
  {
    id: 'client-branches',
    title: 'Sucursales',
    description: 'Plantas/sucursales por cliente, para clientes que distribuyen desde más de una',
    icon: <Building2 className="w-10 h-10" />,
    color: 'text-lime-600',
    bgColor: 'bg-lime-500',
  },
];

interface CatalogItem {
  id: number;
  code: string;
  name: string;
  description?: string | null;
  isActive: boolean;
  [key: string]: any;
}

interface ProcessGroup {
  id: number;
  code: string;
  name: string;
}

export default function CatalogsView() {
  const [selectedCatalog, setSelectedCatalog] = useState<string | null>(null);
  const [items, setItems] = useState<CatalogItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState<CatalogItem | null>(null);
  const [formData, setFormData] = useState<Record<string, any>>({});
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [generalError, setGeneralError] = useState<string>('');
  const [processGroups, setProcessGroups] = useState<ProcessGroup[]>([]);
  const [processTypes, setProcessTypes] = useState<{ id: number; name: string }[]>([]);
  const [mfgProcesses, setMfgProcesses] = useState<{ id: number; code: string; name: string }[]>([]);
  const [selectedGroupId, setSelectedGroupId] = useState<number | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [showCsvModal, setShowCsvModal] = useState(false);
  const [csvImporting, setCsvImporting] = useState(false);
  const [csvResults, setCsvResults] = useState<{ total: number; created: number; updated: number; errors: { row: number; message: string }[] } | null>(null);
  const csvFileRef = useRef<HTMLInputElement>(null);

  // Weight-records sub-concepts management
  const [expandedWeightIds, setExpandedWeightIds] = useState<Set<number>>(new Set());
  const [subConceptsMap, setSubConceptsMap] = useState<Record<number, any[]>>({});
  const [loadingSubIds, setLoadingSubIds] = useState<Set<number>>(new Set());
  const [showSubConceptModal, setShowSubConceptModal] = useState(false);
  const [subConceptParent, setSubConceptParent] = useState<any | null>(null);
  const [subConceptData, setSubConceptData] = useState({ code: '', name: '', description: '', acumulaProductoTerminado: false, acumulaArranque: false });
  const [subConceptSaving, setSubConceptSaving] = useState(false);
  const [editingSubConcept, setEditingSubConcept] = useState<any | null>(null);

  const PAGINATED_CATALOGS = ['clients', 'products'];

  const fetchItems = async (
    catalogId: string,
    search?: string,
    groupId?: number | null,
    category?: string | null,
    page?: number,
    pSize?: number,
    sKey?: string | null,
    sDir?: 'asc' | 'desc'
  ) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      let url = `/api/catalogs/${catalogId}`;
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (groupId) params.append('groupId', groupId.toString());
      if (category) params.append('category', category);
      if (PAGINATED_CATALOGS.includes(catalogId)) {
        params.append('page', String(page ?? currentPage));
        params.append('pageSize', String(pSize ?? pageSize));
        if (sKey) params.append('orderBy', sKey);
        if (sDir) params.append('orderDir', sDir);
      }
      if (params.toString()) url += `?${params.toString()}`;
      
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (PAGINATED_CATALOGS.includes(catalogId) && response.data && 'data' in response.data) {
        setItems(response.data.data);
        setTotalItems(response.data.total);
        setTotalPages(response.data.totalPages);
      } else {
        setItems(response.data);
        setTotalItems(response.data.length);
        setTotalPages(1);
      }
    } catch (error) {
      console.error('Error fetching items:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchProcessGroups = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/catalogs/process-groups', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProcessGroups(response.data);
    } catch (error) {
      console.error('Error fetching process groups:', error);
    }
  };

  const fetchProcessTypes = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/catalogs/process-types', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProcessTypes(response.data);
    } catch (error) {
      console.error('Error fetching process types:', error);
    }
  };

  const fetchMfgProcesses = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/catalogs/manufacturing-processes', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMfgProcesses(response.data);
    } catch (error) {
      console.error('Error fetching manufacturing processes:', error);
    }
  };

  const loadSubConcepts = async (parentId: number) => {
    if (subConceptsMap[parentId] !== undefined) return;
    setLoadingSubIds(prev => new Set(prev).add(parentId));
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/catalogs/weight-records/${parentId}/sub-concepts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubConceptsMap(prev => ({ ...prev, [parentId]: response.data }));
    } catch (error) {
      console.error('Error loading sub-concepts:', error);
      setSubConceptsMap(prev => ({ ...prev, [parentId]: [] }));
    } finally {
      setLoadingSubIds(prev => { const s = new Set(prev); s.delete(parentId); return s; });
    }
  };

  const toggleWeightExpand = (id: number) => {
    setExpandedWeightIds(prev => {
      const s = new Set(prev);
      if (s.has(id)) {
        s.delete(id);
      } else {
        s.add(id);
        loadSubConcepts(id);
      }
      return s;
    });
  };

  const openSubConceptModal = (parent: any, editing?: any) => {
    setSubConceptParent(parent);
    setEditingSubConcept(editing ?? null);
    setSubConceptData(editing
      ? { code: editing.code, name: editing.name, description: editing.description || '', acumulaProductoTerminado: editing.acumulaProductoTerminado ?? false, acumulaArranque: editing.acumulaArranque ?? false }
      : { code: '', name: '', description: '', acumulaProductoTerminado: false, acumulaArranque: false });
    setShowSubConceptModal(true);
  };

  const saveSubConcept = async () => {
    if (!subConceptParent || !subConceptData.code.trim() || !subConceptData.name.trim()) return;
    setSubConceptSaving(true);
    try {
      const token = localStorage.getItem('token');
      const payload = { ...subConceptData, parentId: subConceptParent.id, isActive: true };
      if (editingSubConcept) {
        await axios.put(`/api/catalogs/weight-records/${editingSubConcept.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        await axios.post('/api/catalogs/weight-records', payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      setSubConceptsMap(prev => { const m = { ...prev }; delete m[subConceptParent.id]; return m; });
      await loadSubConcepts(subConceptParent.id);
      setShowSubConceptModal(false);
      setSubConceptParent(null);
      setEditingSubConcept(null);
    } catch (error: any) {
      alert(error?.response?.data?.error || 'Error al guardar subconcepto');
    } finally {
      setSubConceptSaving(false);
    }
  };

  const deleteSubConcept = async (sub: any, parentId: number) => {
    if (!confirm(`¿Eliminar subconcepto "${sub.name}"?`)) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`/api/catalogs/weight-records/${sub.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubConceptsMap(prev => ({ ...prev, [parentId]: (prev[parentId] || []).filter((s: any) => s.id !== sub.id) }));
    } catch (error) {
      alert('Error al eliminar subconcepto');
    }
  };

  const handleSort = (key: string) => {
    if (!selectedCatalog) return;
    const isPaginated = PAGINATED_CATALOGS.includes(selectedCatalog);
    if (sortKey === key) {
      const newDir = sortDir === 'asc' ? 'desc' : 'asc';
      setSortDir(newDir);
      if (isPaginated) {
        setCurrentPage(1);
        fetchItems(selectedCatalog, searchTerm, selectedGroupId, selectedCategory, 1, pageSize, key, newDir);
      }
    } else {
      setSortKey(key);
      setSortDir('asc');
      if (isPaginated) {
        setCurrentPage(1);
        fetchItems(selectedCatalog, searchTerm, selectedGroupId, selectedCategory, 1, pageSize, key, 'asc');
      }
    }
  };

  const handleCatalogClick = async (catalogId: string) => {
    setSelectedCatalog(catalogId);
    setSearchTerm('');
    setSelectedGroupId(null);
    setSelectedCategory(null);
    setSortKey(null);
    setSortDir('asc');
    setCurrentPage(1);
    setPageSize(50);
    setTotalItems(0);
    setTotalPages(1);
    await fetchItems(catalogId, undefined, null, null, 1, 50, null, 'asc');
    
    if (catalogId === 'process-specs') {
      await fetchProcessGroups();
    }
    if (catalogId === 'manufacturing-routes') {
      await fetchProcessTypes();
      await fetchMfgProcesses();
    }
  };

  const handleSearch = (value: string) => {
    setSearchTerm(value);
    if (selectedCatalog) {
      setCurrentPage(1);
      fetchItems(selectedCatalog, value, selectedGroupId, selectedCategory, 1, pageSize, sortKey, sortDir);
    }
  };

  const handleGroupFilter = (groupId: number | null) => {
    setSelectedGroupId(groupId);
    if (selectedCatalog) {
      setCurrentPage(1);
      fetchItems(selectedCatalog, searchTerm, groupId, selectedCategory, 1, pageSize, sortKey, sortDir);
    }
  };

  const handleCategoryFilter = (category: string | null) => {
    setSelectedCategory(category);
    if (selectedCatalog) {
      setCurrentPage(1);
      fetchItems(selectedCatalog, searchTerm, selectedGroupId, category, 1, pageSize, sortKey, sortDir);
    }
  };

  const handlePageChange = (newPage: number) => {
    if (!selectedCatalog || newPage < 1 || newPage > totalPages) return;
    setCurrentPage(newPage);
    fetchItems(selectedCatalog, searchTerm, selectedGroupId, selectedCategory, newPage, pageSize, sortKey, sortDir);
  };

  const handlePageSizeChange = (newSize: number) => {
    if (!selectedCatalog) return;
    setPageSize(newSize);
    setCurrentPage(1);
    fetchItems(selectedCatalog, searchTerm, selectedGroupId, selectedCategory, 1, newSize, sortKey, sortDir);
  };

  const handleBack = () => {
    setSelectedCatalog(null);
    setItems([]);
    setSearchTerm('');
    setSelectedGroupId(null);
    setSelectedCategory(null);
    setSortKey(null);
    setSortDir('asc');
    setCurrentPage(1);
    setPageSize(50);
    setTotalItems(0);
    setTotalPages(1);
  };

  const handleOpenCsvModal = () => {
    setCsvResults(null);
    setShowCsvModal(true);
  };

  const handleDownloadTemplate = () => {
    const header = 'codigo,nombre,secuencia,tipoProceso,procesoFabricacion,descripcion,activo';
    const example = 'RT-001,Extrusión de Película,1,Extrusión,Extrusión Mono,Ruta para extrusión básica,true';
    const blob = new Blob([header + '\n' + example], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'plantilla_rutas_fabricacion.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleCsvImport = async () => {
    const file = csvFileRef.current?.files?.[0];
    if (!file) {
      alert('Selecciona un archivo CSV');
      return;
    }
    setCsvImporting(true);
    setCsvResults(null);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);
      const response = await axios.post('/api/catalogs/manufacturing-routes/import-csv', formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      setCsvResults(response.data);
      await fetchItems('manufacturing-routes', searchTerm, selectedGroupId, selectedCategory, currentPage, pageSize, sortKey, sortDir);
    } catch (error: any) {
      const msg = error?.response?.data?.error || 'Error al importar el CSV';
      setCsvResults({ total: 0, created: 0, updated: 0, errors: [{ row: 0, message: msg }] });
    } finally {
      setCsvImporting(false);
    }
  };

  const handleCreate = () => {
    setEditingItem(null);
    setFormData(getDefaultFormData());
    setFormErrors({});
    setGeneralError('');
    setShowModal(true);
  };

  const handleEdit = (item: CatalogItem) => {
    setEditingItem(item);
    setFormData({ ...item });
    setFormErrors({});
    setGeneralError('');
    setShowModal(true);
  };

  const handleDelete = async (item: CatalogItem) => {
    if (!confirm(`¿Eliminar "${item.name}"?`)) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`/api/catalogs/${selectedCatalog}/${item.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await fetchItems(selectedCatalog!, searchTerm, selectedGroupId, selectedCategory, currentPage, pageSize, sortKey, sortDir);
    } catch (error) {
      console.error('Error deleting item:', error);
      alert('Error al eliminar el registro');
    }
  };

  const handleSave = async () => {
    setFormErrors({});
    setGeneralError('');
    
    try {
      const token = localStorage.getItem('token');
      const payload = { ...formData };
      const notNullNumericFields = ['caliber', 'factor', 'grm2'];
      for (const field of notNullNumericFields) {
        if (payload[field] === '' || payload[field] === undefined || payload[field] === null) {
          payload[field] = '0';
        }
      }
      if (payload['unitOfMeasureId'] === '' || payload['unitOfMeasureId'] === undefined) {
        payload['unitOfMeasureId'] = null;
      }
      if (editingItem) {
        await axios.put(`/api/catalogs/${selectedCatalog}/${editingItem.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        await axios.post(`/api/catalogs/${selectedCatalog}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      setShowModal(false);
      await fetchItems(selectedCatalog!, searchTerm, selectedGroupId, selectedCategory, currentPage, pageSize, sortKey, sortDir);
    } catch (error: any) {
      console.error('Error saving item:', error);
      const responseData = error.response?.data;
      
      if (responseData?.errors && typeof responseData.errors === 'object') {
        setFormErrors(responseData.errors);
        setGeneralError(responseData.error || 'Por favor corrige los errores indicados');
      } else {
        setGeneralError(responseData?.error || responseData?.message || 'Error al guardar el registro');
      }
    }
  };

  const getDefaultFormData = (): Record<string, any> => {
    switch (selectedCatalog) {
      case 'clients':
        return { code: '', name: '', rfc: '', address: '', phone: '', email: '', contactName: '', isActive: true };
      case 'products':
        return { code: '', name: '', description: '', caliber: '', factor: '', medida: '', category: 'PRODUCTO_TERMINADO', unitOfMeasureId: null, isActive: true };
      case 'units':
        return { code: '', name: '', abbreviation: '', isActive: true };
      case 'equipment':
        return { code: '', name: '', description: '', location: '', circunferenciaCilindro: '', anchoImpresion: '', cantidadCilindros: '', velocidadMS: '', eficienciaPct: '', tiempoSetupMin: '', disponibilidadColores: '', mangas: [], equipoGemeloId: '', tomaIndicadoresFlexo: false, isActive: true };
      case 'process-groups':
        return { code: '', name: '', description: '', isActive: true };
      case 'process-specs':
        return { processGroupId: processGroups[0]?.id || 0, code: '', name: '', description: '', specifications: '', isActive: true };
      case 'manufacturing-routes':
        return { code: '', name: '', sequence: '', processType: '', manufacturingProcess: '', description: '', enablesChecklist: false, isVerificationOnly: false, solicitaBobinas: false, registraTiempoCurado: false, isActive: true };
      case 'weight-records':
        return { code: '', name: '', description: '', acumulaProductoTerminado: false, acumulaArranque: false, isActive: true };
      case 'process-types':
        return { code: '', name: '', description: '', enablesLamination: false, enablesExtrusion: false, velocidadCorridoMpm: '', isActive: true };
      case 'manufacturing-processes':
        return { code: '', name: '', tiempoPreparacionMin: '', planningKind: '', curingHours: '', standPouch: false, isActive: true };
      case 'pause-reasons':
        return { code: '', name: '', description: '', isActive: true };
      case 'colors':
        return { code: '', name: '', hexColor: '', isActive: true };
      case 'print-types':
        return { code: '', name: '', description: '', isActive: true };
      case 'photocell-types':
        return { code: '', name: '', description: '', isActive: true };
      case 'employees':
        return { numEmpleado: '', name: '', puesto: '', notas: '', isActive: true };
      case 'client-branches':
        return { customerCode: '', code: '', name: '', isActive: true };
      default:
        return { code: '', name: '', isActive: true };
    }
  };

  const getCatalogTitle = () => {
    return catalogCards.find(c => c.id === selectedCatalog)?.title || 'Catálogo';
  };

  const getFieldError = (field: string) => formErrors[field];
  const getFieldClass = (field: string) => 
    `w-full p-2 border rounded-md ${formErrors[field] ? 'border-red-500 bg-red-50' : 'border-gray-300'}`;

  const renderFormFields = () => {
    const commonFields = (
      <>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Código *</label>
          <input
            type="text"
            value={formData.code || ''}
            onChange={(e) => setFormData({ ...formData, code: e.target.value })}
            className={getFieldClass('code')}
            required
          />
          {getFieldError('code') && (
            <p className="text-red-600 text-sm mt-1">{getFieldError('code')}</p>
          )}
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Nombre *</label>
          <input
            type="text"
            value={formData.name || ''}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            className={getFieldClass('name')}
            required
          />
          {getFieldError('name') && (
            <p className="text-red-600 text-sm mt-1">{getFieldError('name')}</p>
          )}
        </div>
      </>
    );

    switch (selectedCatalog) {
      case 'clients':
        return (
          <>
            {commonFields}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">RFC</label>
              <input
                type="text"
                value={formData.rfc || ''}
                onChange={(e) => setFormData({ ...formData, rfc: e.target.value })}
                className="w-full p-2 border border-gray-300 rounded-md"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Dirección</label>
              <textarea
                value={formData.address || ''}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                className="w-full p-2 border border-gray-300 rounded-md"
                rows={2}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Teléfono</label>
                <input
                  type="text"
                  value={formData.phone || ''}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className={getFieldClass('phone')}
                />
                {getFieldError('phone') && (
                  <p className="text-red-600 text-sm mt-1">{getFieldError('phone')}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  value={formData.email || ''}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className={getFieldClass('email')}
                />
                {getFieldError('email') && (
                  <p className="text-red-600 text-sm mt-1">{getFieldError('email')}</p>
                )}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nombre de Contacto</label>
              <input
                type="text"
                value={formData.contactName || ''}
                onChange={(e) => setFormData({ ...formData, contactName: e.target.value })}
                className="w-full p-2 border border-gray-300 rounded-md"
              />
            </div>
          </>
        );

      case 'products':
        return (
          <>
            {commonFields}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Categoría *</label>
              <select
                value={formData.category || 'PRODUCTO_TERMINADO'}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                className="w-full p-2 border border-gray-300 rounded-md"
              >
                <option value="PRODUCTO_TERMINADO">Producto Terminado</option>
                <option value="MATERIA_PRIMA">Materia Prima</option>
                <option value="TINTAS">Tintas</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Descripción</label>
              <textarea
                value={formData.description || ''}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full p-2 border border-gray-300 rounded-md"
                rows={3}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Calibre</label>
              <input
                type="number"
                step="0.0001"
                value={formData.caliber ?? ''}
                onChange={(e) => setFormData({ ...formData, caliber: e.target.value })}
                className="w-full p-2 border border-gray-300 rounded-md"
                placeholder="0.0000"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Factor (F)</label>
              <input
                type="number"
                step="0.0001"
                value={formData.factor || ''}
                onChange={(e) => setFormData({ ...formData, factor: e.target.value })}
                className="w-full p-2 border border-gray-300 rounded-md"
                placeholder="0.0000"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Medida</label>
              <input
                type="text"
                value={formData.medida || ''}
                onChange={(e) => setFormData({ ...formData, medida: e.target.value })}
                className="w-full p-2 border border-gray-300 rounded-md"
                placeholder="Ej: 80"
                maxLength={100}
              />
            </div>
          </>
        );

      case 'units':
        return (
          <>
            {commonFields}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Abreviatura</label>
              <input
                type="text"
                value={formData.abbreviation || ''}
                onChange={(e) => setFormData({ ...formData, abbreviation: e.target.value })}
                className="w-full p-2 border border-gray-300 rounded-md"
                maxLength={10}
              />
            </div>
          </>
        );

      case 'equipment':
        return (
          <>
            {commonFields}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Descripción</label>
              <textarea
                value={formData.description || ''}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full p-2 border border-gray-300 rounded-md"
                rows={2}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Ubicación</label>
              <input
                type="text"
                value={formData.location || ''}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                className="w-full p-2 border border-gray-300 rounded-md"
              />
            </div>
            <div className="border-t pt-4 mt-4">
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Ancho Impresión (mm)</label>
                  <input
                    type="number"
                    step="0.0001"
                    value={formData.anchoImpresion || ''}
                    onChange={(e) => setFormData({ ...formData, anchoImpresion: e.target.value })}
                    className="w-full p-2 border border-gray-300 rounded-md"
                    placeholder="0.0000"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Velocidad M/M</label>
                  <input
                    type="number"
                    step="0.0001"
                    value={formData.velocidadMS || ''}
                    onChange={(e) => setFormData({ ...formData, velocidadMS: e.target.value })}
                    className="w-full p-2 border border-gray-300 rounded-md"
                    placeholder="0.0000"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Eficiencia promedio (%)</label>
                  <input
                    type="number"
                    min="1"
                    max="100"
                    step="0.01"
                    value={formData.eficienciaPct ?? ''}
                    onChange={(e) => setFormData({ ...formData, eficienciaPct: e.target.value })}
                    className="w-full p-2 border border-gray-300 rounded-md"
                    placeholder="Ej: 80"
                  />
                  <p className="text-xs text-gray-400 mt-1">Considera paros menores y velocidad real. Se usa para estimar el tiempo de fabricación.</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tiempo de setup (min)</label>
                  <input
                    type="number"
                    min="0"
                    step="1"
                    value={formData.tiempoSetupMin ?? ''}
                    onChange={(e) => setFormData({ ...formData, tiempoSetupMin: e.target.value })}
                    className="w-full p-2 border border-gray-300 rounded-md"
                    placeholder="Ej: 45"
                  />
                  <p className="text-xs text-gray-400 mt-1">Montaje, tinteros y mangas por trabajo en esta máquina.</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Disponibilidad de Colores</label>
                  <input
                    type="number"
                    step="1"
                    min="0"
                    value={formData.disponibilidadColores || ''}
                    onChange={(e) => setFormData({ ...formData, disponibilidadColores: e.target.value })}
                    className="w-full p-2 border border-gray-300 rounded-md"
                    placeholder="0"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Máquina Gemela</label>
                  <select
                    value={formData.equipoGemeloId || ''}
                    onChange={(e) => setFormData({ ...formData, equipoGemeloId: e.target.value || null })}
                    className="w-full p-2 border border-gray-300 rounded-md text-sm"
                  >
                    <option value="">— Sin gemela —</option>
                    {items.filter((item: any) => item.id !== formData.id && item.isActive !== false).map((item: any) => (
                      <option key={item.id} value={item.id}>{item.code} — {item.name}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="border-t pt-4">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-medium text-gray-900">Mangas (Cilindros)</h4>
                  <button
                    type="button"
                    onClick={() => {
                      const current = Array.isArray(formData.mangas) ? formData.mangas : [];
                      setFormData({ ...formData, mangas: [...current, { circunferencia: '', cantidad: '' }] });
                    }}
                    className="flex items-center gap-1 px-3 py-1 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700"
                  >
                    <span>+</span> Agregar manga
                  </button>
                </div>
                {Array.isArray(formData.mangas) && formData.mangas.length > 0 ? (
                  <div className="space-y-2">
                    <div className="grid grid-cols-[1fr_1fr_auto] gap-2 text-xs font-medium text-gray-500 px-1">
                      <span>Circunferencia (mm)</span>
                      <span>Piezas disponibles</span>
                      <span></span>
                    </div>
                    {formData.mangas.map((manga: any, idx: number) => (
                      <div key={idx} className="grid grid-cols-[1fr_1fr_auto] gap-2 items-center">
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={manga.circunferencia ?? ''}
                          onChange={(e) => {
                            const updated = formData.mangas.map((m: any, i: number) => i === idx ? { ...m, circunferencia: e.target.value } : m);
                            setFormData({ ...formData, mangas: updated });
                          }}
                          className="p-2 border border-gray-300 rounded-md text-sm"
                          placeholder="Ej: 390"
                        />
                        <input
                          type="number"
                          step="1"
                          min="0"
                          value={manga.cantidad ?? ''}
                          onChange={(e) => {
                            const updated = formData.mangas.map((m: any, i: number) => i === idx ? { ...m, cantidad: e.target.value } : m);
                            setFormData({ ...formData, mangas: updated });
                          }}
                          className="p-2 border border-gray-300 rounded-md text-sm"
                          placeholder="Ej: 8"
                        />
                        <button
                          type="button"
                          onClick={() => {
                            const updated = formData.mangas.filter((_: any, i: number) => i !== idx);
                            setFormData({ ...formData, mangas: updated });
                          }}
                          className="px-2 py-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded text-lg leading-none"
                          title="Eliminar manga"
                        >×</button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-400 italic py-2">Sin mangas registradas. Use el botón "+" para agregar.</p>
                )}
              </div>
            </div>
            <div className="flex items-center justify-between py-2 border-t border-gray-100 mt-2">
              <div>
                <p className="text-sm font-medium text-gray-700">Toma Indicadores Flexo</p>
                <p className="text-xs text-gray-500">Este equipo aporta datos a los KPIs de flexografía</p>
              </div>
              <button
                type="button"
                onClick={() => setFormData({ ...formData, tomaIndicadoresFlexo: !formData.tomaIndicadoresFlexo })}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${formData.tomaIndicadoresFlexo ? 'bg-orange-500' : 'bg-gray-300'}`}
                aria-pressed={!!formData.tomaIndicadoresFlexo}
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${formData.tomaIndicadoresFlexo ? 'translate-x-6' : 'translate-x-1'}`} />
              </button>
            </div>
          </>
        );

      case 'process-groups':
        return (
          <>
            {commonFields}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Descripción</label>
              <textarea
                value={formData.description || ''}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full p-2 border border-gray-300 rounded-md"
                rows={3}
              />
            </div>
          </>
        );

      case 'process-specs':
        return (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Grupo de Proceso *</label>
              <select
                value={formData.processGroupId || ''}
                onChange={(e) => setFormData({ ...formData, processGroupId: parseInt(e.target.value) })}
                className="w-full p-2 border border-gray-300 rounded-md"
                required
              >
                <option value="">Seleccionar grupo...</option>
                {processGroups.map(g => (
                  <option key={g.id} value={g.id}>{g.name}</option>
                ))}
              </select>
            </div>
            {commonFields}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Descripción</label>
              <textarea
                value={formData.description || ''}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full p-2 border border-gray-300 rounded-md"
                rows={2}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Especificaciones</label>
              <textarea
                value={formData.specifications || ''}
                onChange={(e) => setFormData({ ...formData, specifications: e.target.value })}
                className="w-full p-2 border border-gray-300 rounded-md"
                rows={4}
              />
            </div>
          </>
        );

      case 'manufacturing-routes':
        return (
          <>
            {commonFields}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Secuencia</label>
              <input
                type="number"
                min="1"
                step="1"
                value={formData.sequence ?? ''}
                onChange={(e) => setFormData({ ...formData, sequence: e.target.value === '' ? null : parseInt(e.target.value) })}
                className="w-full p-2 border border-gray-300 rounded-md"
                placeholder="Ej. 1, 2, 3..."
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de Proceso</label>
              <select
                value={formData.processType || ''}
                onChange={(e) => setFormData({ ...formData, processType: e.target.value })}
                className="w-full p-2 border border-gray-300 rounded-md"
              >
                <option value="">— Sin tipo de proceso —</option>
                {processTypes.map(pt => (
                  <option key={pt.id} value={pt.name}>{pt.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Proceso de Fabricación</label>
              <select
                value={formData.manufacturingProcess || ''}
                onChange={(e) => setFormData({ ...formData, manufacturingProcess: e.target.value })}
                className="w-full p-2 border border-gray-300 rounded-md"
              >
                <option value="">— Sin proceso de fabricación —</option>
                {mfgProcesses.map(mp => (
                  <option key={mp.id} value={mp.name}>{mp.code} — {mp.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Descripción</label>
              <textarea
                value={formData.description || ''}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full p-2 border border-gray-300 rounded-md"
                rows={3}
              />
            </div>
            <div className="flex items-center gap-3 mt-4">
              <button
                type="button"
                id="enable-checklist-toggle-route"
                onClick={() => setFormData({ ...formData, enablesChecklist: !formData.enablesChecklist })}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${formData.enablesChecklist ? 'bg-green-600' : 'bg-gray-300'}`}
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${formData.enablesChecklist ? 'translate-x-6' : 'translate-x-1'}`} />
              </button>
              <label htmlFor="enable-checklist-toggle-route" className="text-sm font-medium text-gray-700">Habilita CheckList</label>
            </div>
            <div className="flex items-center gap-3 mt-2">
              <button
                type="button"
                id="verification-only-toggle-route"
                onClick={() => setFormData({ ...formData, isVerificationOnly: !formData.isVerificationOnly })}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${formData.isVerificationOnly ? 'bg-purple-600' : 'bg-gray-300'}`}
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${formData.isVerificationOnly ? 'translate-x-6' : 'translate-x-1'}`} />
              </button>
              <label htmlFor="verification-only-toggle-route" className="text-sm font-medium text-gray-700">
                Solo Verificación (sin peso, ej. Curado)
              </label>
            </div>
            <div className="flex items-center gap-3 mt-2">
              <button
                type="button"
                id="solicita-bobinas-toggle-route"
                onClick={() => setFormData({ ...formData, solicitaBobinas: !formData.solicitaBobinas })}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${formData.solicitaBobinas ? 'bg-blue-600' : 'bg-gray-300'}`}
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${formData.solicitaBobinas ? 'translate-x-6' : 'translate-x-1'}`} />
              </button>
              <label htmlFor="solicita-bobinas-toggle-route" className="text-sm font-medium text-gray-700">
                Solicita Bobinas
              </label>
            </div>
            <div className="flex items-center gap-3 mt-2">
              <button
                type="button"
                id="registra-tiempo-curado-toggle-route"
                onClick={() => setFormData({ ...formData, registraTiempoCurado: !formData.registraTiempoCurado })}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${formData.registraTiempoCurado ? 'bg-teal-600' : 'bg-gray-300'}`}
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${formData.registraTiempoCurado ? 'translate-x-6' : 'translate-x-1'}`} />
              </button>
              <label htmlFor="registra-tiempo-curado-toggle-route" className="text-sm font-medium text-gray-700">
                Registra Tiempo de Curado (inicio/fin/duración, ej. Curado)
              </label>
            </div>
          </>
        );

      case 'weight-records':
        return (
          <>
            {commonFields}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Descripción</label>
              <textarea
                value={formData.description || ''}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full p-2 border border-gray-300 rounded-md"
                rows={3}
                placeholder="Descripción del concepto de registro de peso..."
              />
            </div>
          </>
        );

      case 'process-types':
        return (
          <>
            {commonFields}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Descripción</label>
              <textarea
                value={formData.description || ''}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full p-2 border border-gray-300 rounded-md"
                rows={3}
                placeholder="Descripción del tipo de proceso..."
              />
            </div>
            <div className="flex items-center gap-3 mt-4">
              <button
                type="button"
                onClick={() => setFormData({ ...formData, enablesLamination: !formData.enablesLamination })}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${formData.enablesLamination ? 'bg-cyan-500' : 'bg-gray-300'}`}
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${formData.enablesLamination ? 'translate-x-6' : 'translate-x-1'}`} />
              </button>
              <label className="text-sm font-medium text-gray-700">Habilita Laminación</label>
            </div>
            <div className="flex items-center gap-3 mt-4">
              <button
                type="button"
                onClick={() => setFormData({ ...formData, enablesExtrusion: !formData.enablesExtrusion })}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${formData.enablesExtrusion ? 'bg-blue-600' : 'bg-gray-300'}`}
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${formData.enablesExtrusion ? 'translate-x-6' : 'translate-x-1'}`} />
              </button>
              <label className="text-sm font-medium text-gray-700">Habilita Co-Extrusión</label>
            </div>
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Velocidad de corrido (m/min)</label>
              <input
                type="number"
                min="0"
                step="1"
                value={formData.velocidadCorridoMpm ?? ''}
                onChange={(e) => setFormData({ ...formData, velocidadCorridoMpm: e.target.value === '' ? null : e.target.value })}
                className="w-full p-2 border border-gray-300 rounded-md"
                placeholder="Ej: 220"
              />
              <p className="text-xs text-gray-400 mt-1">Se usa para calcular tiempos de planeación cuando Configuración → Planeación está en "Calcula tiempos en base a proceso".</p>
            </div>
          </>
        );

      case 'manufacturing-processes':
        return (
          <>
            {commonFields}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Tiempo de Preparación (min)</label>
              <input
                type="number"
                min="0"
                step="1"
                value={formData.tiempoPreparacionMin ?? ''}
                onChange={(e) => setFormData({ ...formData, tiempoPreparacionMin: e.target.value === '' ? null : Number(e.target.value) })}
                className="w-full p-2 border border-gray-300 rounded-md"
                placeholder="Ej: 30"
              />
              <p className="text-xs text-gray-400 mt-1">Tiempo fijo de arranque/preparación que se suma al tiempo de carrera calculado por velocidad. Si el equipo tiene su propio setup, se usa el del equipo.</p>
            </div>
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Tipo para planeación</label>
              <select
                value={formData.planningKind ?? ''}
                onChange={(e) => setFormData({ ...formData, planningKind: e.target.value === '' ? null : e.target.value })}
                className="w-full p-2 border border-gray-300 rounded-md"
              >
                <option value="">Estándar</option>
                <option value="PRINT">Impresión (setup según colores usados)</option>
                <option value="LAMINATION">Laminación</option>
                <option value="CUT">Corte / refilado (tiempo entre bobinas al eje)</option>
                <option value="EXTRUSION">Extrusión (usa rendimiento kg/h del equipo)</option>
              </select>
              <p className="text-xs text-gray-400 mt-1">Aplica reglas especiales al calcular el tiempo de este proceso en la planeación.</p>
            </div>
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Espera obligatoria después (horas)</label>
              <input
                type="number"
                min="0"
                max="720"
                step="0.5"
                value={formData.curingHours ?? ''}
                onChange={(e) => setFormData({ ...formData, curingHours: e.target.value === '' ? null : e.target.value })}
                className="w-full p-2 border border-gray-300 rounded-md"
                placeholder="Ej: 24 (curado)"
              />
              <p className="text-xs text-gray-400 mt-1">Reposo que debe transcurrir antes del siguiente proceso. Corre las 24 horas, no solo en turno. Si el proceso no usa máquina (por ejemplo, Curado), la espera es su duración.</p>
            </div>
            <div className="flex items-center gap-3 mt-4">
              <button
                type="button"
                onClick={() => setFormData({ ...formData, standPouch: !formData.standPouch })}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${formData.standPouch ? 'bg-cyan-500' : 'bg-gray-300'}`}
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${formData.standPouch ? 'translate-x-6' : 'translate-x-1'}`} />
              </button>
              <label className="text-sm font-medium text-gray-700">Stand Pouch</label>
            </div>
          </>
        );

      case 'colors':
        return (
          <>
            {commonFields}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Color (Hex)</label>
              <div className="flex items-center gap-3">
                <input
                  type="color"
                  value={formData.hexColor || '#ffffff'}
                  onChange={(e) => setFormData({ ...formData, hexColor: e.target.value })}
                  className="w-12 h-10 p-0.5 border border-gray-300 rounded cursor-pointer"
                />
                <input
                  type="text"
                  value={formData.hexColor || ''}
                  onChange={(e) => setFormData({ ...formData, hexColor: e.target.value })}
                  placeholder="#000000"
                  maxLength={7}
                  className="flex-1 p-2 border border-gray-300 rounded-md font-mono text-sm"
                />
              </div>
              <p className="text-xs text-gray-400 mt-1">Opcional. Ejemplo: #FF5733</p>
            </div>
          </>
        );

      case 'employees':
        return (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Núm. Empleado</label>
              <input
                type="number"
                value={formData.numEmpleado || ''}
                onChange={(e) => setFormData({ ...formData, numEmpleado: e.target.value ? parseInt(e.target.value) : null })}
                className="w-full p-2 border border-gray-300 rounded-md"
                placeholder="Número de empleado (opcional)"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nombre *</label>
              <input
                type="text"
                value={formData.name || ''}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className={getFieldClass('name')}
                placeholder="Nombre completo del empleado"
              />
              {getFieldError('name') && <p className="text-red-600 text-sm mt-1">{getFieldError('name')}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Puesto</label>
              <input
                type="text"
                value={formData.puesto || ''}
                onChange={(e) => setFormData({ ...formData, puesto: e.target.value })}
                className="w-full p-2 border border-gray-300 rounded-md"
                placeholder="Ej: Operador de máquina, Supervisor..."
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Notas</label>
              <textarea
                value={formData.notas || ''}
                onChange={(e) => setFormData({ ...formData, notas: e.target.value })}
                className="w-full p-2 border border-gray-300 rounded-md"
                rows={3}
                placeholder="Observaciones adicionales..."
              />
            </div>
          </>
        );

      case 'client-branches':
        return (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Código de Cliente *</label>
              <input
                type="text"
                value={formData.customerCode || ''}
                onChange={(e) => setFormData({ ...formData, customerCode: e.target.value })}
                className={getFieldClass('customerCode')}
                placeholder="Código del cliente tal cual aparece en Órdenes de Producción"
              />
              {getFieldError('customerCode') && <p className="text-red-600 text-sm mt-1">{getFieldError('customerCode')}</p>}
              <p className="text-xs text-gray-400 mt-1">Debe coincidir con el Código de Cliente usado en la orden.</p>
            </div>
            {commonFields}
          </>
        );

      case 'pause-reasons':
        return (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Código *</label>
              <input
                type="text"
                value={formData.code || ''}
                onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                className={getFieldClass('code')}
                required
              />
              {getFieldError('code') && <p className="text-red-600 text-sm mt-1">{getFieldError('code')}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Motivo *</label>
              <input
                type="text"
                value={formData.name || ''}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className={getFieldClass('name')}
                required
                placeholder="Motivo de pausa..."
              />
              {getFieldError('name') && <p className="text-red-600 text-sm mt-1">{getFieldError('name')}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Descripción</label>
              <textarea
                value={formData.description || ''}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full p-2 border border-gray-300 rounded-md"
                rows={3}
                placeholder="Descripción detallada del motivo..."
              />
            </div>
          </>
        );

      case 'print-types':
        return (
          <>
            {commonFields}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Descripción</label>
              <textarea
                value={formData.description || ''}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full p-2 border border-gray-300 rounded-md"
                rows={3}
                placeholder="Descripción del tipo de impresión..."
              />
            </div>
          </>
        );

      case 'photocell-types':
        return (
          <>
            {commonFields}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Descripción</label>
              <textarea
                value={formData.description || ''}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full p-2 border border-gray-300 rounded-md"
                rows={3}
                placeholder="Descripción del tipo de fotocelda..."
              />
            </div>
          </>
        );

      default:
        return commonFields;
    }
  };

  const getTableColumns = (): { key: string; label: string }[] => {
    switch (selectedCatalog) {
      case 'clients':
        return [
          { key: 'code', label: 'Código' },
          { key: 'name', label: 'Nombre' },
          { key: 'rfc', label: 'RFC' },
          { key: 'phone', label: 'Teléfono' },
          { key: 'email', label: 'Email' },
        ];
      case 'products':
        return [
          { key: 'code', label: 'Código' },
          { key: 'name', label: 'Nombre' },
          { key: 'categoryLabel', label: 'Categoría' },
          { key: 'description', label: 'Descripción' },
          { key: 'caliber', label: 'Calibre' },
          { key: 'factor', label: 'Factor (F)' },
          { key: 'medida', label: 'Medida' },
        ];
      case 'units':
        return [
          { key: 'code', label: 'Código' },
          { key: 'name', label: 'Nombre' },
          { key: 'abbreviation', label: 'Abreviatura' },
        ];
      case 'equipment':
        return [
          { key: 'code', label: 'Código' },
          { key: 'name', label: 'Nombre' },
          { key: 'location', label: 'Ubicación' },
          { key: 'circunferenciaCilindro', label: 'Circ. Manga' },
          { key: 'anchoImpresion', label: 'Ancho Imp. (mm)' },
          { key: 'cantidadCilindros', label: 'Cant. Mangas' },
          { key: 'velocidadMS', label: 'Vel. M/M' },
          { key: 'eficienciaPct', label: 'Eficiencia (%)' },
          { key: 'tiempoSetupMin', label: 'Setup (min)' },
          { key: 'disponibilidadColores', label: 'Colores Disponibles' },
          { key: 'tomaIndicadoresFlexo', label: 'Indicadores Flexo' },
        ];
      case 'process-groups':
        return [
          { key: 'code', label: 'Código' },
          { key: 'name', label: 'Nombre' },
          { key: 'description', label: 'Descripción' },
        ];
      case 'process-specs':
        return [
          { key: 'code', label: 'Código' },
          { key: 'name', label: 'Nombre' },
          { key: 'processGroupName', label: 'Grupo' },
          { key: 'description', label: 'Descripción' },
        ];
      case 'manufacturing-routes':
        return [
          { key: 'sequence', label: 'Secuencia' },
          { key: 'code', label: 'Código' },
          { key: 'name', label: 'Nombre' },
          { key: 'processType', label: 'Tipo de Proceso' },
          { key: 'manufacturingProcess', label: 'Proceso de Fabricación' },
          { key: 'description', label: 'Descripción' },
          { key: 'enablesChecklist', label: 'Habilita CheckList' },
          { key: 'isVerificationOnly', label: 'Solo Verificación' },
          { key: 'solicitaBobinas', label: 'Solicita Bobinas' },
          { key: 'registraTiempoCurado', label: 'Registra Tiempo de Curado' },
        ];
      case 'weight-records':
        return [
          { key: 'code', label: 'Código' },
          { key: 'name', label: 'Concepto' },
          { key: 'description', label: 'Descripción' },
        ];
      case 'process-types':
        return [
          { key: 'code', label: 'Código' },
          { key: 'name', label: 'Nombre' },
          { key: 'description', label: 'Descripción' },
          { key: 'enablesLamination', label: 'Habilita Laminación' },
          { key: 'enablesExtrusion', label: 'Habilita Co-Extrusión' },
          { key: 'velocidadCorridoMpm', label: 'Velocidad Corrido (m/min)' },
        ];
      case 'manufacturing-processes':
        return [
          { key: 'code', label: 'Código Proceso' },
          { key: 'name', label: 'Nombre' },
          { key: 'tiempoPreparacionMin', label: 'Prep. (min)' },
          { key: 'curingHours', label: 'Espera (h)' },
          { key: 'standPouch', label: 'Stand Pouch' },
        ];
      case 'pause-reasons':
        return [
          { key: 'code', label: 'Código' },
          { key: 'name', label: 'Motivo' },
          { key: 'description', label: 'Descripción' },
        ];
      case 'print-types':
        return [
          { key: 'code', label: 'Código' },
          { key: 'name', label: 'Nombre' },
          { key: 'description', label: 'Descripción' },
        ];
      case 'photocell-types':
        return [
          { key: 'code', label: 'Código' },
          { key: 'name', label: 'Nombre' },
          { key: 'description', label: 'Descripción' },
        ];
      case 'colors':
        return [
          { key: 'code', label: 'Código' },
          { key: 'name', label: 'Nombre' },
          { key: 'hexColor', label: 'Color' },
        ];
      case 'employees':
        return [
          { key: 'id', label: 'Clave' },
          { key: 'numEmpleado', label: 'Núm. Empleado' },
          { key: 'name', label: 'Nombre' },
          { key: 'puesto', label: 'Puesto' },
          { key: 'notas', label: 'Notas' },
        ];
      case 'client-branches':
        return [
          { key: 'customerCode', label: 'Código de Cliente' },
          { key: 'code', label: 'Código' },
          { key: 'name', label: 'Nombre' },
        ];
      default:
        return [
          { key: 'code', label: 'Código' },
          { key: 'name', label: 'Nombre' },
        ];
    }
  };

  const categoryLabels: Record<string, string> = {
    'PRODUCTO_TERMINADO': 'Producto Terminado',
    'MATERIA_PRIMA': 'Materia Prima',
    'TINTAS': 'Tintas'
  };

  const isPaginatedCatalog = selectedCatalog ? PAGINATED_CATALOGS.includes(selectedCatalog) : false;

  const sortedItems = useMemo(() => {
    if (isPaginatedCatalog) return items;
    if (!sortKey) return items;
    return [...items].sort((a, b) => {
      const aRaw = sortKey === 'categoryLabel'
        ? (categoryLabels[a.category] || a.category || '')
        : (a[sortKey] ?? '');
      const bRaw = sortKey === 'categoryLabel'
        ? (categoryLabels[b.category] || b.category || '')
        : (b[sortKey] ?? '');
      const aVal = typeof aRaw === 'number' ? aRaw : String(aRaw).toLowerCase();
      const bVal = typeof bRaw === 'number' ? bRaw : String(bRaw).toLowerCase();
      if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
  }, [items, sortKey, sortDir, isPaginatedCatalog]);

  // Main catalog cards view
  if (!selectedCatalog) {
    return (
      <div className="p-6">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Catálogos del Sistema</h1>
          <p className="text-gray-600 mt-1">Gestión de catálogos maestros del sistema</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {catalogCards.map((catalog) => (
            <div
              key={catalog.id}
              onClick={() => handleCatalogClick(catalog.id)}
              className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden cursor-pointer hover:shadow-lg hover:border-gray-300 transition-all duration-200"
            >
              <div className={`${catalog.bgColor} p-6 flex justify-center items-center`}>
                <div className="text-white">
                  {catalog.icon}
                </div>
              </div>
              <div className="p-4">
                <h3 className="font-semibold text-gray-900 text-lg">{catalog.title}</h3>
                <p className="text-sm text-gray-500 mt-1">{catalog.description}</p>
                <div className="mt-3">
                  <span className="text-blue-600 text-sm font-medium hover:text-blue-800 inline-flex items-center">
                    Administrar →
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Catalog detail view with CRUD
  const columns = getTableColumns();

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Button variant="outline" onClick={handleBack}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Volver
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{getCatalogTitle()}</h1>
            <p className="text-gray-600 text-sm">
              {isPaginatedCatalog ? `${totalItems} registros` : `${items.length} registros`}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {selectedCatalog === 'manufacturing-routes' && (
            <Button variant="outline" onClick={handleOpenCsvModal} className="border-green-600 text-green-700 hover:bg-green-50">
              <Upload className="w-4 h-4 mr-2" />
              Importar CSV
            </Button>
          )}
          <Button onClick={handleCreate} className="bg-blue-600 hover:bg-blue-700">
            <Plus className="w-4 h-4 mr-2" />
            Nuevo
          </Button>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
        <div className="flex flex-wrap gap-4">
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Buscar por código o nombre..."
                value={searchTerm}
                onChange={(e) => handleSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
          
          {selectedCatalog === 'process-specs' && (
            <div className="min-w-[200px]">
              <select
                value={selectedGroupId || ''}
                onChange={(e) => handleGroupFilter(e.target.value ? parseInt(e.target.value) : null)}
                className="w-full p-2 border border-gray-300 rounded-md"
              >
                <option value="">Todos los grupos</option>
                {processGroups.map(g => (
                  <option key={g.id} value={g.id}>{g.name}</option>
                ))}
              </select>
            </div>
          )}
          
          {selectedCatalog === 'products' && (
            <div className="min-w-[200px]">
              <select
                value={selectedCategory || ''}
                onChange={(e) => handleCategoryFilter(e.target.value || null)}
                className="w-full p-2 border border-gray-300 rounded-md"
              >
                <option value="">Todas las categorías</option>
                <option value="PRODUCTO_TERMINADO">Producto Terminado</option>
                <option value="MATERIA_PRIMA">Materia Prima</option>
                <option value="TINTAS">Tintas</option>
              </select>
            </div>
          )}

          {isPaginatedCatalog && (
            <div className="flex items-center gap-2 ml-auto">
              <span className="text-sm text-gray-500 whitespace-nowrap">Mostrar:</span>
              <select
                value={pageSize}
                onChange={(e) => handlePageSizeChange(parseInt(e.target.value))}
                className="p-2 border border-gray-300 rounded-md text-sm"
              >
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-500">Cargando...</div>
        ) : items.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            No hay registros. Haz clic en "Nuevo" para agregar uno.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Acciones
                  </th>
                  {columns.map((col) => (
                    <th
                      key={col.key}
                      onClick={() => handleSort(col.key)}
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer select-none hover:bg-gray-100 transition-colors"
                    >
                      <div className="flex items-center gap-1">
                        {col.label}
                        {sortKey === col.key ? (
                          sortDir === 'asc'
                            ? <ChevronUp className="w-3.5 h-3.5 text-blue-600" />
                            : <ChevronDown className="w-3.5 h-3.5 text-blue-600" />
                        ) : (
                          <ChevronsUpDown className="w-3.5 h-3.5 text-gray-300" />
                        )}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {sortedItems.map((item) => {
                  const displayItem: Record<string, any> = {
                    ...item,
                    categoryLabel: categoryLabels[item.category] || item.category || '-'
                  };
                  const isWeightRecord = selectedCatalog === 'weight-records';
                  const isExpanded = isWeightRecord && expandedWeightIds.has(item.id);
                  const subList = subConceptsMap[item.id] ?? [];
                  const isLoadingSub = loadingSubIds.has(item.id);
                  return (
                    <React.Fragment key={item.id}>
                      <tr className={`hover:bg-gray-50 ${isExpanded ? 'bg-amber-50' : ''}`}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          {isWeightRecord && (
                            <button
                              onClick={() => toggleWeightExpand(item.id)}
                              title={isExpanded ? 'Colapsar subconceptos' : 'Ver subconceptos'}
                              className="mr-1 p-1 rounded hover:bg-amber-100 text-amber-600"
                            >
                              <ChevronRight className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                            </button>
                          )}
                          <Button variant="ghost" size="sm" onClick={() => handleEdit(item)}>
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDelete(item)} className="text-red-600 hover:text-red-800">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </td>
                        {columns.map((col) => (
                          <td key={col.key} className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {col.key === 'hexColor'
                              ? (displayItem.hexColor
                                  ? <div className="flex items-center gap-2">
                                      <span
                                        className="inline-block w-6 h-6 rounded border border-gray-300 shadow-sm flex-shrink-0"
                                        style={{ backgroundColor: displayItem.hexColor }}
                                      />
                                      <span className="font-mono text-xs text-gray-600">{displayItem.hexColor}</span>
                                    </div>
                                  : <span className="text-gray-400">—</span>)
                              : typeof displayItem[col.key] === 'boolean'
                                ? (displayItem[col.key]
                                    ? <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">Sí</span>
                                    : <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-500">No</span>)
                                : (displayItem[col.key] != null && displayItem[col.key] !== '' ? displayItem[col.key] : '-')}
                          </td>
                        ))}
                      </tr>
                      {isWeightRecord && isExpanded && (
                        <tr>
                          <td colSpan={columns.length + 1} className="px-0 py-0 bg-amber-50 border-b border-amber-100">
                            <div className="px-8 py-3">
                              <div className="flex items-center justify-between mb-2">
                                <span className="text-xs font-semibold text-amber-700 flex items-center gap-1">
                                  <ListTree className="w-3.5 h-3.5" />
                                  Subconceptos de "{item.name}"
                                </span>
                                <button
                                  onClick={() => openSubConceptModal(item)}
                                  className="flex items-center gap-1 text-xs bg-amber-600 text-white px-2 py-1 rounded hover:bg-amber-700"
                                >
                                  <Plus className="w-3 h-3" />
                                  Agregar Subconcepto
                                </button>
                              </div>
                              {isLoadingSub ? (
                                <p className="text-xs text-gray-500 italic">Cargando...</p>
                              ) : subList.length === 0 ? (
                                <p className="text-xs text-gray-400 italic">Sin subconceptos. Haz clic en "Agregar Subconcepto".</p>
                              ) : (
                                <table className="w-full text-xs border border-amber-200 rounded-lg overflow-hidden">
                                  <thead>
                                    <tr className="bg-amber-100 text-amber-800">
                                      <th className="px-3 py-1.5 text-left font-semibold">Acciones</th>
                                      <th className="px-3 py-1.5 text-left font-semibold">Código</th>
                                      <th className="px-3 py-1.5 text-left font-semibold">Subconcepto</th>
                                      <th className="px-3 py-1.5 text-left font-semibold">Descripción</th>
                                      <th className="px-3 py-1.5 text-center font-semibold" title="Acumula Producto Terminado">Prod. Term.</th>
                                    </tr>
                                  </thead>
                                  <tbody className="bg-white divide-y divide-amber-100">
                                    {subList.map((sub: any) => (
                                      <tr key={sub.id} className={`hover:bg-amber-50 ${sub.acumulaProductoTerminado ? 'bg-green-50' : ''}`}>
                                        <td className="px-3 py-1.5 whitespace-nowrap">
                                          <button onClick={() => openSubConceptModal(item, sub)} className="p-1 rounded hover:bg-gray-100 text-gray-500 hover:text-blue-600" title="Editar">
                                            <Edit className="w-3.5 h-3.5" />
                                          </button>
                                          <button onClick={() => deleteSubConcept(sub, item.id)} className="p-1 rounded hover:bg-gray-100 text-gray-500 hover:text-red-600" title="Eliminar">
                                            <Trash2 className="w-3.5 h-3.5" />
                                          </button>
                                        </td>
                                        <td className="px-3 py-1.5 font-mono text-gray-600">{sub.code}</td>
                                        <td className="px-3 py-1.5 font-medium text-gray-800">{sub.name}</td>
                                        <td className="px-3 py-1.5 text-gray-500">{sub.description || '—'}</td>
                                        <td className="px-3 py-1.5 text-center">
                                          {sub.acumulaProductoTerminado
                                            ? <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700">✓ Sí</span>
                                            : <span className="text-gray-300 text-xs">—</span>}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {isPaginatedCatalog && totalPages > 1 && (
        <div className="flex items-center justify-between mt-4 px-1">
          <p className="text-sm text-gray-600">
            Página {currentPage} de {totalPages} &mdash; {totalItems} registros en total
          </p>
          <div className="flex items-center gap-1">
            <button
              onClick={() => handlePageChange(1)}
              disabled={currentPage === 1}
              className="px-2 py-1 rounded border border-gray-300 text-sm text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
            >«</button>
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="px-2 py-1 rounded border border-gray-300 text-sm text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
            >‹</button>
            {Array.from({ length: Math.min(7, totalPages) }, (_, i) => {
              let pg: number;
              if (totalPages <= 7) {
                pg = i + 1;
              } else if (currentPage <= 4) {
                pg = i + 1;
              } else if (currentPage >= totalPages - 3) {
                pg = totalPages - 6 + i;
              } else {
                pg = currentPage - 3 + i;
              }
              return (
                <button
                  key={pg}
                  onClick={() => handlePageChange(pg)}
                  className={`px-2.5 py-1 rounded border text-sm ${
                    pg === currentPage
                      ? 'bg-blue-600 text-white border-blue-600 font-medium'
                      : 'border-gray-300 text-gray-600 hover:bg-gray-100'
                  }`}
                >{pg}</button>
              );
            })}
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="px-2 py-1 rounded border border-gray-300 text-sm text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
            >›</button>
            <button
              onClick={() => handlePageChange(totalPages)}
              disabled={currentPage === totalPages}
              className="px-2 py-1 rounded border border-gray-300 text-sm text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
            >»</button>
          </div>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="text-lg font-semibold">
                {editingItem ? 'Editar Registro' : 'Nuevo Registro'}
              </h2>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              {generalError && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
                  {generalError}
                </div>
              )}
              {renderFormFields()}
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="isActive"
                  checked={formData.isActive !== false}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <label htmlFor="isActive" className="ml-2 text-sm text-gray-700">Activo</label>
              </div>
            </div>
            <div className="flex justify-end gap-2 p-4 border-t">
              <Button variant="outline" onClick={() => setShowModal(false)}>
                Cancelar
              </Button>
              <Button onClick={handleSave} className="bg-blue-600 hover:bg-blue-700">
                Guardar
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Sub-Concept Modal */}
      {showSubConceptModal && subConceptParent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="flex items-center justify-between p-4 border-b bg-gradient-to-r from-amber-50 to-yellow-50">
              <div>
                <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                  <ListTree className="w-5 h-5 text-amber-600" />
                  {editingSubConcept ? 'Editar Subconcepto' : 'Nuevo Subconcepto'}
                </h2>
                <p className="text-xs text-amber-700 mt-0.5">Concepto padre: <span className="font-semibold">{subConceptParent.name}</span></p>
              </div>
              <button onClick={() => { setShowSubConceptModal(false); setSubConceptParent(null); setEditingSubConcept(null); }} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Código <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  value={subConceptData.code}
                  onChange={(e) => setSubConceptData({ ...subConceptData, code: e.target.value })}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                  placeholder="Ej: SC-001"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nombre <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  value={subConceptData.name}
                  onChange={(e) => setSubConceptData({ ...subConceptData, name: e.target.value })}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                  placeholder="Nombre del subconcepto..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Descripción</label>
                <textarea
                  value={subConceptData.description}
                  onChange={(e) => setSubConceptData({ ...subConceptData, description: e.target.value })}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                  rows={2}
                  placeholder="Descripción opcional..."
                />
              </div>
              <div className="flex items-start gap-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                <input
                  type="checkbox"
                  id="acumulaPT"
                  checked={!!subConceptData.acumulaProductoTerminado}
                  onChange={(e) => setSubConceptData({ ...subConceptData, acumulaProductoTerminado: e.target.checked })}
                  className="mt-0.5 h-4 w-4 rounded border-gray-300 text-green-600 focus:ring-green-500"
                />
                <div>
                  <label htmlFor="acumulaPT" className="block text-sm font-semibold text-green-800 cursor-pointer">
                    Acumula Producto Terminado
                  </label>
                  <p className="text-xs text-green-600 mt-0.5">
                    Los pesos registrados con este subconcepto se contabilizarán en el % de avance de producción de la orden.
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2 mt-2">
                <input
                  type="checkbox"
                  id="acumulaArranque"
                  checked={!!subConceptData.acumulaArranque}
                  onChange={(e) => setSubConceptData({ ...subConceptData, acumulaArranque: e.target.checked })}
                  className="w-4 h-4 accent-amber-500"
                />
                <label htmlFor="acumulaArranque" className="text-sm text-gray-700">Acumula Arranque</label>
              </div>
            </div>
            <div className="flex justify-end gap-2 p-4 border-t">
              <Button variant="outline" onClick={() => { setShowSubConceptModal(false); setSubConceptParent(null); setEditingSubConcept(null); }}>
                Cancelar
              </Button>
              <Button
                onClick={saveSubConcept}
                disabled={subConceptSaving || !subConceptData.code.trim() || !subConceptData.name.trim()}
                className="bg-amber-600 hover:bg-amber-700"
              >
                {subConceptSaving ? 'Guardando...' : 'Guardar Subconcepto'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* CSV Import Modal */}
      {showCsvModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg">
            <div className="flex items-center justify-between p-5 border-b">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Importar Rutas de Fabricación</h2>
                <p className="text-sm text-gray-500 mt-0.5">Carga masiva desde archivo CSV</p>
              </div>
              <button onClick={() => setShowCsvModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-5 space-y-5">
              {/* Template download */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
                <Download className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-blue-800">Plantilla CSV</p>
                  <p className="text-xs text-blue-600 mt-0.5">
                    Descarga la plantilla con las columnas correctas antes de importar.
                  </p>
                  <button
                    onClick={handleDownloadTemplate}
                    className="mt-2 text-xs font-medium text-blue-700 underline hover:text-blue-900"
                  >
                    Descargar plantilla_rutas_fabricacion.csv
                  </button>
                </div>
              </div>

              {/* Column reference */}
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Columnas soportadas</p>
                <div className="grid grid-cols-2 gap-1 text-xs text-gray-600">
                  <span><span className="font-medium text-red-600">codigo</span> — Requerido (máx 50)</span>
                  <span><span className="font-medium text-red-600">nombre</span> — Requerido</span>
                  <span><span className="font-medium">secuencia</span> — Número entero</span>
                  <span><span className="font-medium">tipoProceso</span> — Texto libre</span>
                  <span><span className="font-medium">procesoFabricacion</span> — Texto libre</span>
                  <span><span className="font-medium">descripcion</span> — Texto</span>
                  <span><span className="font-medium">activo</span> — true/false</span>
                </div>
                <p className="text-xs text-gray-400 mt-2">Acepta separadores por coma (,) o punto y coma (;). Si el código ya existe, se actualiza el registro.</p>
              </div>

              {/* File input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Seleccionar archivo CSV</label>
                <input
                  ref={csvFileRef}
                  type="file"
                  accept=".csv,text/csv"
                  className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-green-50 file:text-green-700 hover:file:bg-green-100 border border-gray-300 rounded-md"
                />
              </div>

              {/* Results */}
              {csvResults && (
                <div className="space-y-2">
                  <div className="flex gap-3">
                    <div className="flex-1 bg-green-50 border border-green-200 rounded-lg p-3 text-center">
                      <p className="text-2xl font-bold text-green-700">{csvResults.created}</p>
                      <p className="text-xs text-green-600">Creados</p>
                    </div>
                    <div className="flex-1 bg-blue-50 border border-blue-200 rounded-lg p-3 text-center">
                      <p className="text-2xl font-bold text-blue-700">{csvResults.updated}</p>
                      <p className="text-xs text-blue-600">Actualizados</p>
                    </div>
                    <div className="flex-1 bg-red-50 border border-red-200 rounded-lg p-3 text-center">
                      <p className="text-2xl font-bold text-red-700">{csvResults.errors.length}</p>
                      <p className="text-xs text-red-600">Errores</p>
                    </div>
                  </div>

                  {csvResults.errors.length > 0 && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3 max-h-32 overflow-y-auto">
                      {csvResults.errors.map((e, i) => (
                        <div key={i} className="flex items-start gap-2 text-xs text-red-700 mb-1">
                          <AlertCircle className="w-3 h-3 mt-0.5 flex-shrink-0" />
                          <span>{e.row > 0 ? `Fila ${e.row}: ` : ''}{e.message}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  {csvResults.created + csvResults.updated > 0 && csvResults.errors.length === 0 && (
                    <div className="flex items-center gap-2 text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg p-3">
                      <CheckCircle className="w-4 h-4" />
                      Importación completada exitosamente
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="flex justify-end gap-2 p-4 border-t">
              <Button variant="outline" onClick={() => setShowCsvModal(false)}>
                {csvResults ? 'Cerrar' : 'Cancelar'}
              </Button>
              {!csvResults && (
                <Button
                  onClick={handleCsvImport}
                  disabled={csvImporting}
                  className="bg-green-600 hover:bg-green-700"
                >
                  {csvImporting ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                      Importando...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4 mr-2" />
                      Importar
                    </>
                  )}
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
